import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useParentingPlan } from "../../contexts/ParentingPlanContext";
import { Heading, Subheading } from "../../design/heading";
import LoadingView from "../../components/LoadingView";
import DeleteParentingPlanButton from "./components/DeleteParentingPlanButton";
import moment from "moment";
import { useUser } from "../../contexts/UserContext";
import Breadcrumbs from "../../components/Breadcrumbs";
import { useTeamMember } from "../../contexts/TeamMemberContext";
import { Text } from "../../design/text";
import ParentingPlanButton from "../../components/ParentingPlanButton";
import { ParentingPlanEditor } from "./components/ParentingPlanEditor";
import { Navbar, NavbarItem } from "../../design/navbar";
import { ParentingPlanSection } from "../../models/ParentingPlanSection";
import { Button } from "../../design/button";
import { ParentingPlanPreview } from "./components/ParentingPlanPreview";

const ParentingPlanDetailPage: React.FC = () => {
  const { currentUser } = useUser();
  const { teamMembers } = useTeamMember();
  const { updateParentingPlan } = useParentingPlan();
  const { parentingPlanId } = useParams<{ parentingPlanId: string }>();
  const { parentingPlan, setSelectedParentingPlan } = useParentingPlan();
  const [state, setState] = useState<"Edit" | "Preview">("Edit");

  const [sections, setSections] = useState<ParentingPlanSection[]>([]);

  useEffect(() => {
    if (!parentingPlanId || !currentUser) return;
    setSelectedParentingPlan(parentingPlanId);
  }, [parentingPlanId, currentUser]);

  useEffect(() => {
    if (parentingPlan?.sections) {
      setSections(parentingPlan.sections);
    }
  }, [parentingPlan]);

  if (!parentingPlan) {
    return <LoadingView />;
  }

  const updatedBy = (teamMembers ?? [])
    .map((a) => a.user)
    .find((b) => b?._id === parentingPlan.updatedBy);

  const handleSave = async () => {
    try {
      await updateParentingPlan(parentingPlan._id, {
        ...parentingPlan,
        sections: sections,
      });
      alert("Parenting plan saved successfully.");
    } catch (err) {
      console.error("Failed to save parenting plan:", err);
      alert("Failed to save.");
    }
  };

  return (
    <div className="">
      <Breadcrumbs
        items={[
          { label: "Parenting Plans", href: "/parenting-plans" },
          {
            label: parentingPlan.name,
            href: `/parenting-plan/${parentingPlan._id}`,
          },
        ]}
      />
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-2">
                <Heading>{parentingPlan.name}</Heading>
                {parentingPlan.description && (
                  <Text>{parentingPlan.description}</Text>
                )}
                <Subheading>
                  Updated by{" "}
                  {updatedBy?.firstName ?? parentingPlan.updatedBy?.firstName} @{" "}
                  {moment(parentingPlan.updatedAt).format("lll")}
                </Subheading>
              </div>
              <div className="flex gap-2">
                <ParentingPlanButton parentingPlan={parentingPlan} />
                <Button onClick={handleSave}>Save</Button>
                <DeleteParentingPlanButton
                  parentingPlanId={parentingPlan._id}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="flex">
              <div className="flex-grow">
                <Navbar className="mb-4">
                  {(["Edit", "Preview"] as const).map((item) => (
                    <NavbarItem
                      key={item}
                      className="text-base"
                      onClick={() => setState(item)}
                      current={state === item}
                    >
                      {item}
                    </NavbarItem>
                  ))}
                </Navbar>
                {state === "Edit" && (
                  <ParentingPlanEditor
                    sections={sections}
                    onSectionsChange={setSections}
                  />
                )}
                {state === "Preview" && (
                  <ParentingPlanPreview sections={sections} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentingPlanDetailPage;
